import CategoryView from "../treatment/CategoryView";
import AnalysedCriterion from "../treatment/AnalysedCriterion";
import Treatment from "../treatment/Treatment";
import TreatmentSelectionModel from "../treatment/TreatmentSelectionModel";
import TreatmentSelection from "../treatment/TreatmentSelection";
import Satisfaction from "../Satisfaction";
import ReportStatus from "./ReportStatus";
import Scan from "../Scan";
import Answer from "../Answer";
import { PatientCard } from "../patient/PatientCard";
import SmileSimulationTreatmentSettings from "../../../settings/SmileSimulationTreatmentSettings";
import FlowType from "../FlowType";
import ReportComplexity from "./ReportComplexity";

// LongTermFix sjahan 24/09/19: This class needs proper splitting and renaming.
export default interface Report {
  reportUid: string;
  subject: PatientCard;
  observations: AnalysedCriterion[];
  categories: CategoryView[];
  treatments: Treatment[];
  treatmentSelections: TreatmentSelection[];
  scanPackages: ScanPackages;
  note: string;
  noteMaxLength: number;
  noteTextAreaRows: number;
  creation: string;
  status?: ReportStatus;
  patientAnswers: Answer[];
  canEdit: boolean;
  clinicalScore?: number;
  reportComplexity?: ReportComplexity;
  displayClinicalScore?: boolean;
}

export interface ReportModel {
  reportUid: string;
  subject: PatientCard;
  observations: AnalysedCriterion[];
  categories: CategoryView[];
  treatments: Treatment[];
  treatmentSelections: TreatmentSelectionModel[];
  smileSimulation: SmileSimulation;
  scanPackages: ScanPackages;
  note: string;
  noteMaxLength: number;
  noteTextAreaRows: number;
  patientHasRequestedBooking: boolean;
  satisfaction?: Satisfaction;
  creation: string;
  status?: ReportStatus;
  patientAnswers: Answer[];
  canEdit: boolean;
  patientOfReport: boolean;
  galleryPhotos: Scan[];
  reportType: FlowType;
  flowUrl?: string;
  canRetry: boolean;
  clinicalScore?: number;
  reportComplexity?: ReportComplexity;
  displayClinicalScore?: boolean;
}

export interface SmileSimulation {
  enabled: boolean;
  smileWithApplianceAvailable: boolean;
  smileWithApplianceErrorDescription?: string;
  smilePredictionAvailable: boolean;
  smilePredictionErrorDescription?: string;
  settings: SmileSimulationTreatmentSettings;
}

export interface ScanPackages {
  opened: Scan[];
  closed: Scan[];
  slightlyOpened?: Scan[];
  face?: Scan[];
}

export enum SmileSimulationPhotoCategory {
  SMILE_WITH_APPLIANCE = "SMILE_WITH_APPLIANCE",
  SMILE_PREDICTION = "SMILE_PREDICTION",
}

export interface ErrorTypeMessage {
  error: string;
  message?: string;
}

export interface AssessmentResultObservationCategoryModel {
  id: string;
  label: string;
  criteria: AnalysedCriterion[];
}

export const getCriteriaByCategory = function(
  report: Report,
): AssessmentResultObservationCategoryModel[] {
  return report.observations
    .reduce((res: AssessmentResultObservationCategoryModel[], criterion) => {
      const category = findOrCreateCategoryById(report, res, criterion.categoryId);
      if (!category.criteria) category.criteria = [];
      category.criteria.push(criterion);
      return res;
    }, [])
    .sort(byCategoryOrder(report.categories));
};

export const assembleReport = function(reportModel: ReportModel): Report {
  const criterionById = reportModel.observations.reduce(
    (acc, elt) => ((acc[elt.criterionId] = elt), acc),
    {},
  );
  const treatmentById = reportModel.treatments.reduce(
    (acc, elt) => ((acc[elt.uid] = elt), acc),
    {},
  );
  return {
    reportUid: reportModel.reportUid,
    subject: reportModel.subject,
    observations: reportModel.observations,
    categories: reportModel.categories,
    treatments: reportModel.treatments,
    scanPackages: reportModel.scanPackages,
    treatmentSelections: reportModel.treatmentSelections.map((it: TreatmentSelectionModel) => ({
      uid: it.uid,
      criteriaGroup: it.criteriaGroup,
      criteria: it.criterionIds.map((id) => criterionById[id]),
      selectedTreatments: it.selectedTreatmentIds.map((id) => treatmentById[id]),
      availableTreatments: it.availableTreatmentIds.map((id) => treatmentById[id]),
      autoAssignedTreatments: it.autoAssignedTreatmentIds.map((id) => treatmentById[id]),
      alerts: it.alerts,
      apiUrl: it.apiUrl,
    })),
    note: reportModel.note,
    noteMaxLength: reportModel.noteMaxLength,
    noteTextAreaRows: reportModel.noteTextAreaRows,
    creation: reportModel.creation,
    status: reportModel.status,
    patientAnswers: reportModel.patientAnswers,
    canEdit: reportModel.canEdit,
    clinicalScore: reportModel.clinicalScore,
    reportComplexity: reportModel.reportComplexity,
    displayClinicalScore: reportModel.displayClinicalScore,
  };
};

export const getTreatmentCardsByCategory = function(report: Report) {
  return report.treatmentSelections
    .filter((treatmentSelection: TreatmentSelection) =>
      treatmentSelection.criteria.some((c) => c.isTreated),
    )
    .reduce((res, treatmentSelection: TreatmentSelection) => {
      const category = findOrCreateCategoryById(
        report,
        res,
        treatmentSelection.criteria[0].categoryId,
      );
      if (!category.selections) category.selections = [];
      category.selections.push(treatmentSelection);
      category.alerts = treatmentSelection.alerts;
      return res;
    }, [])
    .sort(byCategoryOrder(report.categories));
};

const findOrCreateCategoryById = function(report: Report, categories: any[], categoryId: string) {
  let category = categories.find((category) => category.id == categoryId);
  if (!category) {
    category = {
      ...report.categories.find((category) => category.id === categoryId),
    };
    categories.push(category);
  }
  return category;
};

const byCategoryOrder = (categories: CategoryView[]) => (c1: CategoryView, c2: CategoryView) =>
  categories.findIndex((c) => c.id === c1.id) - categories.findIndex((c) => c.id === c2.id);
